import React from 'react';
import {useSelector} from 'react-redux';
import './LobbyListDesktop.css';
// Нижняя полоса — та же, что у десктопного лобби (кнопки и раскладка групп)
import '../../LobbyPage/desktop/LobbyControlBar.css';
import PageButton from '../../elements/PageButton/PageButton';
import LobbyListCard from './LobbyListCard';
import {useSecondTick} from '../../../hooks/react/useSecondTick';
import {checkIfLobbyListStatusTicking} from '../lobbyListStatus';
import {RANKED_STAR_ICON_PATHS, RANKED_STAR_KINDS} from '../../../utils/matches/rankedStarIcon';

/**
 * Раздел списка: заголовок с числом лобби и прокручиваемое содержимое
 */
const LobbyListSection = ({className, title, count, isLoading, emptyContent, children}) => (
  <section className={`lobby-list-section ${className}`}>
    <h2 className="lobby-list-section_title">
      {title}
      {!isLoading && <span className="lobby-list-section_count">{count}</span>}
    </h2>
    <div className="lobby-list-section_body">
      {isLoading && <p className="lobby-list-section_placeholder">Загрузка...</p>}
      {!isLoading && count === 0 && emptyContent}
      {!isLoading && count > 0 && children}
    </div>
  </section>
);

/**
 * Список лобби на десктопе (ширина от 1024 px) — по образцу десктопного лобби
 * (LobbyDesktop): шапка, содержимое на всю высоту окна, нижняя полоса кнопок.
 * Слева — лобби в сборе крупными карточками с местами игроков, справа — идущие
 * матчи строками. В полосе — «Назад», «Рейтинговый матч» (к настройке поиска)
 * и «Создать обычное лобби».
 *
 * Данные, вход в лобби, модалки и аналитика — у LobbyListPage: здесь только
 * раскладка
 *
 * @param {Object} props
 * @param {Object[]} props.preparationLobbies - лобби в сборе (ИИ-лобби первым)
 * @param {Object[]} props.startedLobbies - идущие матчи (ИИ-лобби первым)
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {React.ReactNode} props.infoText - пояснение под заголовком
 * @param {boolean} props.isGuest
 * @param {boolean} props.isViewerAdmin
 * @param {boolean} props.isAdmin
 * @param {function(Object): void} props.onLobbyClick
 * @param {function(): void} props.onCreate
 * @param {function(): void} props.onRankedMatch
 * @param {function(): void} props.onBack
 */
const LobbyListDesktop = ({
  preparationLobbies, startedLobbies, isLoading, error, infoText,
  isGuest, isViewerAdmin, isAdmin, onLobbyClick, onCreate, onRankedMatch, onBack,
}) => {
  // Поправка к часам сервера; без нее (стор без serverTime) — свои часы
  const serverTimeDelta = useSelector((state) => state.private.serverTime?.serverTimeDelta) || 0;
  // Отсчеты этапов (баны, выбор фракций, сбор ИИ-лобби) тикают раз в секунду
  useSecondTick([...preparationLobbies, ...startedLobbies].some(checkIfLobbyListStatusTicking));
  const now = Date.now() + serverTimeDelta;

  const renderCard = (lobby) => (
    <LobbyListCard
      key={lobby.lobbyId}
      lobby={lobby}
      now={now}
      isGuest={isGuest}
      isViewerAdmin={isViewerAdmin}
      isAdmin={isAdmin}
      onClick={onLobbyClick}
    />
  );

  return (
    <div className="lobby-list-desktop">
      <header className="lobby-list-desktop_header">
        <h1 className="lobby-list-desktop_title">Список лобби</h1>
        <p className="lobby-list-desktop_info">
          <img className="lobby-list-desktop_info-icon" src="/img/icons/info.png" alt="" aria-hidden="true"/>
          <span>{infoText}</span>
        </p>
        {error && <p className="lobby-error lobby-list-desktop_error">{error}</p>}
      </header>

      <div className="lobby-list-desktop_main">
        <LobbyListSection
          className="lobby-list-section_gathering"
          title="Сбор игроков"
          count={preparationLobbies.length}
          isLoading={isLoading}
          emptyContent={(
            <div className="lobby-list-section_empty">
              <span>Лобби в сборе нет</span>
              <span className="lobby-list-section_empty-hint">
                Создайте свое — друзей можно позвать по ссылке
              </span>
            </div>
          )}
        >
          <div className="lobby-list-section_grid">{preparationLobbies.map(renderCard)}</div>
        </LobbyListSection>

        <LobbyListSection
          className="lobby-list-section_started"
          title="Начатые матчи"
          count={startedLobbies.length}
          isLoading={isLoading}
          emptyContent={<div className="lobby-list-section_empty">Начатых матчей нет</div>}
        >
          <div className="lobby-list-section_rows">{startedLobbies.map(renderCard)}</div>
        </LobbyListSection>
      </div>

      <div className="lobby-control-bar">
        <div className="lobby-control-bar_group lobby-control-bar_left">
          <PageButton
            className="lobby-bar-button" onClick={onBack} pageId="LobbyListPage" buttonId="back" isGuest={isGuest}
          >Назад</PageButton>
        </div>
        <div className="lobby-control-bar_group lobby-control-bar_right">
          <PageButton
            className="lobby-bar-button lobby-list-bar_ranked"
            onClick={onRankedMatch}
            pageId="LobbyListPage"
            buttonId="ranked_match"
            isGuest={isGuest}
          >
            <img className="lobby-list-bar_ranked-icon" src={RANKED_STAR_ICON_PATHS[RANKED_STAR_KINDS.DEFAULT]} alt=""/>
            Рейтинговый матч
          </PageButton>
          <PageButton
            className="lobby-bar-button lobby-bar-button_primary"
            onClick={onCreate}
            pageId="LobbyListPage"
            buttonId="create_lobby"
            isGuest={isGuest}
          >+ Создать обычное лобби</PageButton>
        </div>
      </div>
    </div>
  );
};

export default LobbyListDesktop;
